import {AgentNewsDto} from 'Agent/AgentInformation/api';
import * as AgentNewsAdministrationRoutConstants from 'AgentNewsAdministration/AgentNewsAdministrationRouteConstants';
import * as NewsApi from 'AgentNewsAdministration/api/agentNewsApi';
import {
  AgentNewsEntryActionType,
  getNewsEntry,
  loadNewsEntry,
  saveNewsEntry,
  showError,
} from 'AgentNewsAdministration/ducks/newsEntry';
import {NewsEntryDto} from 'AgentNewsAdministration/models/Dtos/AgentNewsEntry';
import {InvalidNewsEntry, ValidNewsEntry} from 'AgentNewsAdministration/models/News';
import {push} from 'connected-react-router';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult} from 'Core/models/Async';
import {NonEmptyString} from 'Core/models/EmptyString';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Action} from 'redux';
import {all, call, put, takeLatest} from 'redux-saga/effects';

function* saveNewsEntryToApi(action: ReturnType<typeof saveNewsEntry>) {
  const {newsEntry} = action.payload;

  const saveApiCall = pipe(
    newsEntry.id,
    O.fold(
      () => call(NewsApi.addAgentNewsEntry, newsEntry),
      id => call(NewsApi.updateAgentNewsEntry, id, newsEntry)
    )
  );

  const result: AsyncResult<unknown> = yield saveApiCall;

  const actionToTake = yield pipe(
    result,
    E.fold(
      (error): Action => showError(error.message, newsEntry),
      () => put(push(AgentNewsAdministrationRoutConstants.NewsAdministration))
    )
  );

  yield actionToTake;
}

export function* saveNewsEntrySaga() {
  yield takeLatest(AgentNewsEntryActionType.SAVE_NEWS_ENTRY, saveNewsEntryToApi);
}

function* getNewsEntryFromApi(action: ReturnType<typeof getNewsEntry>) {
  const {id} = action.payload;

  const result: AsyncResult<AgentNewsDto> = yield NewsApi.getAgentNewsEntry(id);

  const toValidatedNewsEntry = (newsDto: NewsEntryDto) =>
    ValidNewsEntry.create(
      new InvalidNewsEntry(
        O.fromNullable(newsDto.id),
        NonEmptyString.create(newsDto.title || ''),
        NonEmptyString.create(newsDto.description || '')
      )
    );

  const actionToTake = pipe(
    result,
    E.map(toValidatedNewsEntry),
    E.fold(
      (): unknown =>
        all([
          put(push(AgentNewsAdministrationRoutConstants.NewsAdministration)),
          put(createSnackbar('There Was a Problem Loading the News Entry.')),
        ]),
      newsEntry => put(loadNewsEntry(newsEntry))
    )
  );

  yield actionToTake;
}

export function* getNewsEntrySaga() {
  yield takeLatest(AgentNewsEntryActionType.GET_NEWS_ENTRY, getNewsEntryFromApi);
}

export default function*() {
  yield all([saveNewsEntrySaga(), getNewsEntrySaga()]);
}
